import { useState, useCallback } from "react";
import {
  DeliverySessionState,
  UseQRDeliveryReturn,
  OrderItemResponse,
} from "@/types/qrDelivery.types";
import { purchaseListService } from "@/services";

/**
 * Hook personalizado para el módulo QR Delivery
 *
 * Gestiona todo el estado y lógica del flujo de entrega:
 * - Validación de códigos QR de estudiantes
 * - Procesamiento de entregas
 * - Gestión de productos seleccionados
 * - Estados de carga y errores
 */
export const useQRDelivery = (): UseQRDeliveryReturn => {
  // Estado principal de la sesión
  const [state, setState] = useState<DeliverySessionState>({
    qrValidation: null,
    deliverySession: null,
    availableProducts: [],
    selectedItems: [],
    error: null,
    flowState: "idle",
  });

  // Estados de carga
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // ============================================================================
  // ACCIONES PRINCIPALES
  // ============================================================================

  /**
   * Valida un código QR de estudiante y obtiene información del pedido
   */
  const validateQR = useCallback(
    async (qrCode: string): Promise<boolean> => {
      try {
        setIsLoading(true);
        setState((prev) => ({ ...prev, flowState: "validating", error: null }));

        // Llamar al endpoint real del backend
        const result = await purchaseListService.processStudentRedemption(qrCode);

        if (result.success && result.data) {
          // Convertir la respuesta del backend al formato esperado por el frontend
          const qrValidation = {
            purchaseListId: qrCode, // Usar el QR como ID
            parentName: result.data.studentName, // El estudiante es quien genera el QR
            productName: result.data.products.map(p => p.productName).join(", "),
            availableDeliveries: result.data.products.map(product => ({
              assignmentId: product.assignmentId,
              studentName: result.data?.studentName || "",
              productName: product.productName,
              remainingQuantity: product.quantity,
              delivered: product.delivered,
            })),
          };

          setState((prev) => ({
            ...prev,
            qrValidation,
            flowState: "selecting_products",
            error: null,
          }));

          return true;
        } else {
          setState((prev) => ({
            ...prev,
            flowState: "error",
            error: result.error || "Error al validar código QR",
          }));
          return false;
        }
      } catch (error: any) {
        setState((prev) => ({
          ...prev,
          flowState: "error",
          error: error.message || "Error al validar código QR",
        }));
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Procesa una entrega de productos de estudiante
   */
  const processDelivery = useCallback(
    async (
      qrCode: string,
      // assignmentId: string,
      // cajeroId: string
    ): Promise<boolean> => {
      try {
        setIsLoading(true);
        setState((prev) => ({ ...prev, flowState: "processing", error: null }));

        // Para el flujo de estudiantes, el procesamiento ya se hizo en validateQR
        // Solo necesitamos marcar como completado
        setState((prev) => ({
          ...prev,
          deliverySession: {
            orderId: qrCode,
            deliveryId: `delivery-${Date.now()}`,
            status: "completed",
            timestamp: new Date().toISOString(),
          },
          flowState: "completed",
          error: null,
        }));

        return true;
      } catch (error: any) {
        setState((prev) => ({
          ...prev,
          flowState: "error",
          error: "Error al procesar entrega",
        }));
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Completa una entrega
   */
  const completeDelivery = useCallback(
    async (): Promise<boolean> => {
      try {
        setIsLoading(true);
        setState((prev) => ({ ...prev, flowState: "completing", error: null }));

        // Para el flujo de estudiantes, la entrega ya está completada
        // Solo agregamos notas si es necesario
        setState((prev) => ({
          ...prev,
          flowState: "completed",
          error: null,
        }));

        return true;
      } catch (error: any) {
        setState((prev) => ({
          ...prev,
          flowState: "error",
          error: "Error al completar entrega",
        }));
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // ============================================================================
  // ACCIONES SECUNDARIAS
  // ============================================================================

  /**
   * Obtiene productos disponibles (no necesario para el flujo de estudiantes)
   */
  const getAvailableProducts = useCallback(async (): Promise<
    OrderItemResponse[]
  > => {
    setIsLoadingProducts(true);
    try {
      // Para el flujo de estudiantes, los productos ya vienen en el QR
      const products: OrderItemResponse[] = [];
      setState((prev) => ({ ...prev, availableProducts: products }));
      return products;
    } catch (error) {
      return [];
    } finally {
      setIsLoadingProducts(false);
    }
  }, []);

  /**
   * Obtiene historial de entregas
   */
  const getDeliveryHistory = useCallback(
    async (): Promise<any[]> => {
      setIsLoadingHistory(true);
      try {
        // Simulación de historial
        await new Promise((resolve) => setTimeout(resolve, 500));
        return [];
      } catch (error) {
        return [];
      } finally {
        setIsLoadingHistory(false);
      }
    },
    []
  );

  // ============================================================================
  // UTILIDADES
  // ============================================================================

  /**
   * Resetea el estado
   */
  const resetState = useCallback(() => {
    setState({
      qrValidation: null,
      deliverySession: null,
      availableProducts: [],
      selectedItems: [],
      error: null,
      flowState: "idle",
    });
  }, []);

  /**
   * Selecciona un item
   */
  const selectItem = useCallback((orderItemId: string, quantity: number) => {
    setState((prev) => {
      const existingItem = prev.selectedItems.find(
        (item) => item.orderItemId === orderItemId
      );
      if (existingItem) {
        return {
          ...prev,
          selectedItems: prev.selectedItems.map((item) =>
            item.orderItemId === orderItemId
              ? { ...item, quantityToDeliver: quantity }
              : item
          ),
        };
      } else {
        return {
          ...prev,
          selectedItems: [
            ...prev.selectedItems,
            { orderItemId, quantityToDeliver: quantity },
          ],
        };
      }
    });
  }, []);

  /**
   * Deselecciona un item
   */
  const deselectItem = useCallback((orderItemId: string) => {
    setState((prev) => ({
      ...prev,
      selectedItems: prev.selectedItems.filter(
        (item) => item.orderItemId !== orderItemId
      ),
    }));
  }, []);

  /**
   * Actualiza la cantidad de un item
   */
  const updateItemQuantity = useCallback(
    (orderItemId: string, quantity: number) => {
      selectItem(orderItemId, quantity);
    },
    [selectItem]
  );

  /**
   * Limpia el error
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    // Estado
    state,

    // Acciones principales
    validateQR,
    processDelivery,
    completeDelivery,

    // Acciones secundarias
    getAvailableProducts,
    getDeliveryHistory,

    // Utilidades
    resetState,
    selectItem,
    deselectItem,
    updateItemQuantity,

    // Estados de carga
    isLoading,
    isLoadingProducts,
    isLoadingHistory,

    // Estados de error
    error: state.error,
    clearError,
  };
};
